import { SECURITY_CHECK_ACL } from '@/security/constants/securityCheck';

import ToFormDataSerializationStrategy from './serialization/ToFormDataSerializationStrategy';
import ToJSONSerializationStrategy from './serialization/ToJSONSerializationStrategy';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD' | 'COPY' | 'LINK' | 'UNLINK';

export type ResponseType = 'json' | 'text' | 'blob' | 'blob-or-json';

export type SerializationStrategy = typeof ToJSONSerializationStrategy | typeof ToFormDataSerializationStrategy;

export type QueryMiddleware = (query: Query) => unknown;

export interface FailSettings {
  requestTimeout?: Nullable<number>;
  retryTimeout?: Nullable<number>;
}

export default class Query {
  base: Nullable<string> = null;
  path: string = '';
  method: Nullable<HttpMethod> = null;
  requestBody: Nullable<Record<string, Any>> = null;
  queryStringParams: Record<string, unknown> = {};
  serializer: SerializationStrategy = ToJSONSerializationStrategy;
  result!: Any;
  headers: Record<string, string> = {};
  requestMiddlewares: QueryMiddleware[] = [];
  responseMiddlewares: QueryMiddleware[] = [];
  suppressErrorNotifications: boolean = false;
  repeatRequestTimeout: number = 0;
  repeatRequestRetryTimeout: number = 0;
  securityCheck: string = SECURITY_CHECK_ACL;
  responseType: ResponseType = 'json';
  isAnonymous = false;
  abortSignal: Nullable<AbortSignal> = null;

  addQueryStringParam(key: string, value: unknown): void {
    this.queryStringParams[key] = value;
  }

  addFailConfiguration(failSettings: FailSettings): void {
    const requestTimeout = Number(failSettings.requestTimeout);
    const retryTimeout = Number(failSettings.retryTimeout);

    if (typeof requestTimeout === 'number' && (requestTimeout === 0 || requestTimeout > 0)) {
      this.repeatRequestTimeout = requestTimeout;
    }

    if (typeof retryTimeout === 'number' && (retryTimeout === 0 || retryTimeout > 0)) {
      this.repeatRequestRetryTimeout = retryTimeout;
    }
  }

  addHeader(key: string, value: string): void {
    this.headers[key] = value;
  }

  addRequestMiddleware(middleware: QueryMiddleware): void {
    this.requestMiddlewares.push(middleware);
  }

  addResponseMiddleware(middleware: QueryMiddleware): void {
    this.responseMiddlewares.push(middleware);
  }

  setSecurityCheck(securityCheck: string): void {
    this.securityCheck = securityCheck;
  }

  getCacheId(): string {
    return this.path;
  }
}
